import Box from '@mui/material/Box';
import { HelpPanel, type HelpContent } from './portfolio_definition/HelpPanel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  EmptyState,
  ModuleTile,
  PageHeader,
  StatusChip,
  getModuleIcon,
} from '@platform/ui-kit';
import { MODULE_GROUP_LABELS, MODULE_GROUP_ORDER } from '@platform/entitlements';
import type { InstallInfo, PortalModule, SessionUser } from '../api';

export interface StartMenuProps {
  modules: PortalModule[];
  install?: InstallInfo;
  user?: SessionUser;
  onOpen: (path: string) => void;
}

function TileGrid({ modules, onOpen }: { modules: PortalModule[]; onOpen: (p: string) => void }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(340px, 1fr))' },
      }}
    >
      {modules.map((module) => {
        const Icon = getModuleIcon(module.icon);
        // ON or OFF is decided by the LICENCE alone (owner's ruling, 2026-08-03).
        // `comingSoon` still rides along, but it is only a chip: it no longer greys the
        // tile or disables it. A module the owner has switched on looks switched on
        // whether or not its screens exist yet.
        const locked = module.state === 'locked';
        return (
          <ModuleTile
            key={module.id}
            name={module.name}
            description={module.description}
            icon={<Icon />}
            locked={locked}
            comingSoon={!locked && module.status === 'planned'}
            onOpen={() => onOpen(module.path)}
          />
        );
      })}
    </Box>
  );
}

/**
 * The portal start menu — the launcher a user lands on after signing in.
 *
 * Tiles come from this install's entitlements crossed with the user's own module
 * assignments. Unlicensed modules stay visible but locked; modules the user has no
 * seat for are absent entirely, because that is a permission boundary rather than a
 * purchasing decision.
 */
/**
 * The launcher's own "How this works" panel.
 *
 * Every module screen has one; the screen people land on first did not, which is the wrong
 * way round — this is the one page where a new user has no context at all. It answers what
 * the platform is, why some tiles are greyed out, and where to go first.
 *
 * Written from what the launcher actually does, not aspirationally: the tile states and the
 * per-user visibility rule below are the real behaviour of MODULE_CATALOG + entitlements.
 */
const START_MENU_HELP: HelpContent = {
  title: 'How this works',
  icon: 'home',
  purpose:
    'This is the <strong>launcher</strong>. Each tile is a product that shares one sign-in, one database and one set of platform services &mdash; user administration, planning, roadmap and the executive dashboard are provided once, at platform level, rather than separately inside each product.',
  doHere: [
    '<strong>Click a tile</strong> to open that product. You land on its start menu, and the left navigation switches to that product only.',
    'Use the <strong>breadcrumb</strong> at the top of any screen to come back here.',
    'A tile you cannot open is either <strong>not licensed for this install</strong> or <strong>not assigned to your account</strong>. The chip on the tile says which.',
  ],
  fits:
    'Products are <strong>independent</strong>: they do not share screens, menus or product data with each other. What they do share is the shell you are looking at, the sign-in, and the platform-level services above. Work done in one product does not appear inside another unless a platform service carries it there deliberately.',
  firstTime:
    'Open the product you were given access to and read its start menu &mdash; every product opens on a page that explains its own sequence before you touch any data.',
  sections: [
    {
      type: 'html',
      label: 'Why a tile might be greyed out',
      html: 'Two different reasons, and the fix differs. <strong>Not licensed</strong> means the install does not have that product &mdash; an administrator changes that under Administration. <strong>No access</strong> means the install has it but your account has not been assigned a seat &mdash; an administrator grants that under Administration &rarr; Users.',
    },
    {
      type: 'html',
      label: 'What the counts mean',
      html: 'The chip beside your name counts the modules <strong>you</strong> can open, not the modules that exist. Two people signing into the same install can legitimately see different numbers.',
    },
  ],
};

export function StartMenu({ modules, install, user, onOpen }: StartMenuProps) {
  const availableCount = modules.filter((m) => m.state === 'available').length;
  const groups = MODULE_GROUP_ORDER.map((group) => ({
    label: MODULE_GROUP_LABELS[group],
    modules: modules.filter((m) => m.group === group),
  })).filter((g) => g.modules.length > 0);

  const firstName = user?.displayName.split(' ')[0];

  return (
    <Box>
      <PageHeader
        title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
        description="Choose a module to get started."
        meta={
          <>
            {install ? <StatusChip tone="info" label={install.clientName} /> : null}
            {install ? <StatusChip tone="neutral" label={install.environment} /> : null}
            <StatusChip tone="neutral" label={`${availableCount} modules available`} />
          </>
        }
        actions={<HelpPanel help={START_MENU_HELP} />}
      />

      {modules.length === 0 ? (
        <EmptyState
          title="No modules are available to you"
          description="This install has no licensed modules, or none has been assigned to your account. An administrator can grant access under Administration → Users."
        />
      ) : (
        <Stack spacing={4}>
          {groups.map((group) => (
            <Box key={group.label}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                {group.label}
              </Typography>
              <TileGrid modules={group.modules} onOpen={onOpen} />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
